// src/pages/PurchaseRecord/components/PurchaseRecordTable.jsx
import React, { useMemo, useEffect, useState } from "react";
import { Table, Button, Tooltip, Popconfirm, Grid, Modal, Descriptions } from "antd";
import {
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  InboxOutlined,
  RedoOutlined,
  BarcodeOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";


import { deletePurchase, fetchPurchaseRecord, retryTatmeenPurchase, unpackPurchase } from "../PurchaseRecordThunks";
import { useNavigate } from "react-router-dom";
import { accessChecker, formatToCompanyTimezone, numberFormatTax } from "../../../../../lib/functions";
import { useIsAdminUser } from "../../../../../lib/accessList";
import { setDetails, setList } from "../PurchaseRecordSlice";
import { useTxLock } from "../../../../../hooks/useTxLock";
import VoucherPdfActions from "../../../../../components/common/VoucherPdfActions";
import { usePurchaseBarcodesPrint } from "../../Purchase Entry/components/usePurchaseBarcodesPrint";
import "../../../../../components/common/bt.css";

const PRINT_COLUMN_WEIGHTS = {
  "No.": 5,

  "Voucher No": 16,

  Date: 7,

  "Supplier / Creditor": 18,
  Supplier: 18,

  "Invoice No": 8,
  "Created By": 8,

  "Sub Total": 7,
  Discount: 8,
  GST: 6,
  VAT: 6,
  Service: 7,
  Total: 7,
  Paid: 7,
  Due: 7,

  Item: 12,
  "Item Code": 10,
  Rate: 6,
  Qty: 5,
  "Discount%": 6,
  "VAT%": 6,

  "Tag Group": 8,
  "Tag Field": 8,
};

const getPrintColWidths = (columns) => {
  const weights = columns.map((col) => PRINT_COLUMN_WEIGHTS[col.title] || 7);
  const sum = weights.reduce((acc, w) => acc + w, 0);
  return weights.map((w) => (w / sum) * 100);
};


const PurchaseRecordTable = ({ printMode = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, loading, filters } = useSelector(
    (state) => state.purchaseRecord,
  );
  const [tatmeenModal, setTatmeenModal] = useState({
    open: false,
    record: null,
  });
  const [retryLoading, setRetryLoading] = useState(false);
  console.log(printMode, "printMode123123");

  // const {loading, filters } = useSelector((state) => state.purchaseRecord);
  const authInfo = useSelector((state) => state.auth?.auth_info);

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isGstEnabled = authInfo?.gst_enabled === "yes";
  const showGstColumns = isIndia && isGstEnabled;
  const showTax = !isIndia || isGstEnabled;
  const taxLabel = isIndia ? "GST" : "VAT";
  const isTatmeenUAE =
    authInfo?.is_tatmeen_enabled === "yes" &&
    authInfo?.country === "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');

  const hasTatmeenEntryAccess = accessChecker("tatmeen_entry");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;


  const { useBreakpoint } = Grid;

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const handleTableChange = (p) => setPagination(p);

  const money = (v) => Number(v || 0).toFixed(2);

  const handleDeleteCaller = async (record) => {
    const res = await dispatch(deletePurchase(record.pur_id));
    if (res.meta.requestStatus === "fulfilled") {
      dispatch(setList([]));
      dispatch(setDetails([]));
      dispatch(fetchPurchaseRecord({ selectedFilterType: filters.filterType, selectedRecordType: filters.recordType, filters }));
    } else {
      Swal.fire(res.payload || "Something went wrong", "", "warning");
    }
  };

  const handleTatmeenRetry = async (record) => {
    const result = await Swal.fire({
      title: "Retry Tatmeen Sync?",
      text: `Purchase ${record.vch_no} will be reprocessed.`,
      icon: "question",
      showCancelButton: true,
    });

    if (!result.isConfirmed) return;

    try {
      setRetryLoading(true);
      const res = await dispatch(
        retryTatmeenPurchase(record.pur_id)
      );
      setRetryLoading(false);
      if (res.meta.requestStatus === "fulfilled") {
        Swal.fire(
          "Success",
          "Tatmeen retry has been queued successfully.",
          "success"
        );

        setTatmeenModal({
          open: false,
          record: null,
        });

        dispatch(
          fetchPurchaseRecord({
            selectedFilterType: filters.filterType,
            selectedRecordType: filters.recordType,
            filters,
          })
        );
      } else {
        Swal.fire(
          "Failed",
          res.payload || "Retry failed",
          "error"
        );
      }
    } catch (err) {
      Swal.fire(
        "Failed",
        err.message,
        "error"
      );
    } finally {
      setRetryLoading(false);
    }
  };

  const handleTatmeenUnpack = async (record) => {
    const result = await Swal.fire({
      title: "Unpack Tatmeen Sync?",
      text: `Purchase ${record.vch_no}  will be processed for unpacking.`,
      icon: "question",
      showCancelButton: true,
    });

    if (!result.isConfirmed) return;

    try {
      setRetryLoading(true);
      const res = await dispatch(
        unpackPurchase(record.pur_id)
      );
      setRetryLoading(false);
      if (res.meta.requestStatus === "fulfilled") {
        Swal.fire(
          "Success",
          "Tatmeen unpack has been queued successfully.",
          "success"
        );

        setTatmeenModal({
          open: false,
          record: null,
        });

        dispatch(
          fetchPurchaseRecord({
            selectedFilterType: filters.filterType,
            selectedRecordType: filters.recordType,
            filters,
          })
        );
      } else {
        Swal.fire(
          "Failed",
          res.payload || "Retry failed",
          "error"
        );
      }
    } catch (err) {
      Swal.fire(
        "Failed",
        err.message,
        "error"
      );
    } finally {
      setRetryLoading(false);
    }
  };

  const openTatmeenModal = (record) => {
    setTatmeenModal({
      open: true,
      record,
    });
  };

  // ✅ Reusable actions component with is_locked support
  const PurchaseActions = ({ record }) => {
    const isLocked = Number(record?.is_locked) === 1;
    const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
    const txLocked = isTxLocked(record?.date);
    const [unpackLoading, setUnpackLoading] = useState(false);

    const { printBarcodesForPurchase, loading: barcodeLoading } = usePurchaseBarcodesPrint();

    const canEditAction =
      !isLocked &&
      !txLocked &&
      (isAdminUser || canEdit);

    const canDeleteAction =
      !isLocked &&
      !txLocked &&
      (isAdminUser || canDelete);

    return (
      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>

        {isTatmeenUAE && hasTatmeenEntryAccess && (
          <Tooltip title="Tatmeen Details">
            <Button
              type="link"
              icon={<InboxOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openTatmeenModal(record);
              }}
            />
          </Tooltip>
        )}

        {/* View */}
        <Tooltip title="View Voucher">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/purchase/quick-purchase-voucher/${record.pur_id}`);
            }}
          />
        </Tooltip>


        {/* {isTatmeenUAE && (
          <Tooltip
            title={
              record.tatmeen_sent_status !== "failed"
                ? "Purchase is not in failed state"
                : record.tatmeen_manual_action_required !== "yes"
                  ? "Manual action not required"
                  : "Retry Tatmeen Sync"
            }
          >
            <Button
              type="link"
              disabled={
                record.tatmeen_manual_action_required !== "yes"
              }
              icon={<RedoOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleTatmeenRetry(record);
              }}
            />
          </Tooltip>
        )} */}

        {/* Edit */}
        <Tooltip
          title={
            isLocked ? "Cannot edit. Used in a Purchase Return"
              : txLocked ? `Locked until ${lockDateFormatted}`
                : "Edit Purchase"
          }
        >
          <Button
            type="link"
            disabled={!canEditAction}
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              if (isLocked || txLocked) return;
              navigate(`/purchase/edit/${record.pur_id}`);
            }}
            style={{ color: (isLocked || txLocked) ? "#d9d9d9" : undefined }}
          />
        </Tooltip>


        {/* Delete */}
        <Tooltip
          title={
            isLocked ? "Cannot delete. Used in a Purchase Return"
              : txLocked ? `Locked until ${lockDateFormatted}`
                : "Delete Purchase"
          }
        >
          <Popconfirm
            title="Are you sure you want to delete this purchase?"
            okText="Yes, Delete"
            cancelText="Cancel"
            disabled={!canDeleteAction}
            onConfirm={() => !isLocked && !txLocked && handleDeleteCaller(record)}
            onCancel={(e) => e?.stopPropagation()}
          >
            <Button
              type="link"
              danger
              disabled={!canDeleteAction}
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
              style={{ color: (isLocked || txLocked) ? "#d9d9d9" : undefined }}
            />
          </Popconfirm>
        </Tooltip>

        {/* ✅ Barcodes */}
        <Tooltip title="Print Barcodes">
          <Button
            type="link"
            icon={<BarcodeOutlined />}
            loading={barcodeLoading}
            style={{ color: "#1677ff" }}
            onClick={(e) => {
              e.stopPropagation();
              Swal.fire({
                title: "Select barcode size",
                icon: "question",
                showDenyButton: true,
                confirmButtonText: "Standard (48×30 mm)",
                denyButtonText: "Small (24×15 mm)",
                showCancelButton: true,
                cancelButtonText: "Cancel",
              }).then((result) => {
                if (result.isConfirmed || result.isDenied) {
                  const size = result.isConfirmed ? "standard" : "small";
                  printBarcodesForPurchase(
                    record.pur_id,
                    record.vch_no || "",
                    (msg) => Swal.fire("Barcode Error", msg, "warning"),
                    size
                  );
                }
              });
            }}
          />
        </Tooltip>

        <VoucherPdfActions
          url={`/print/purchase/${record.pur_id}`}
          fileName={`Purchase_${record.vch_no || record.pur_id}.pdf`}
        />

      </div>
    );
  };

  // ─── withItem view actions (simpler — view + delete + barcode) ──────────────
  const WithItemActions = ({ record }) => {
    const isLocked = Number(record?.is_locked) === 1;
    const { printBarcodesForPurchase, loading: barcodeLoading } = usePurchaseBarcodesPrint();

    return (
      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
        <Tooltip title="View Voucher">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/purchase/quick-purchase-voucher/${record.pur_id}`);
            }}
          />
        </Tooltip>

        {isAdminUser && (
          <Tooltip title={isLocked ? "Cannot delete. Used in Purchase Return" : "Delete Purchase"}>
            <Popconfirm
              title="Are you sure you want to delete this purchase?"
              okText="Yes, Delete"
              cancelText="Cancel"
              disabled={isLocked}
              onConfirm={() => !isLocked && handleDeleteCaller(record)}
              onCancel={(e) => e?.stopPropagation()}
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e?.stopPropagation()}
                style={{ color: isLocked ? "#d9d9d9" : undefined }}
              />
            </Popconfirm>
          </Tooltip>
        )}

        {/* ✅ Barcodes */}
        <Tooltip title="Print Barcodes">
          <Button
            type="link"
            icon={<BarcodeOutlined />}
            loading={barcodeLoading}
            style={{ color: "#1677ff" }}
            onClick={(e) => {
              e.stopPropagation();
              Swal.fire({
                title: "Select barcode size",
                icon: "question",
                showDenyButton: true,
                confirmButtonText: "Standard (48×30 mm)",
                denyButtonText: "Small (24×15 mm)",
                showCancelButton: true,
                cancelButtonText: "Cancel",
              }).then((result) => {
                if (result.isConfirmed || result.isDenied) {
                  const size = result.isConfirmed ? "standard" : "small";
                  printBarcodesForPurchase(
                    record.pur_id,
                    record.vch_no || "",
                    (msg) => Swal.fire("Barcode Error", msg, "warning"),
                    size
                  );
                }
              });
            }}
          />
        </Tooltip>

        <VoucherPdfActions
          url={`/print/purchase/${record.pur_id}`}
          fileName={`Purchase_${record.vch_no || record.pur_id}.pdf`}
        />
      </div>
    );
  };

  // render: (_, record) => {
  //   const subTotal = Number(record.sub_total || 0);
  //   const discount = Number(record.total_discount || 0);
  //   const tax = Number(record.total_tax || 0);
  //   return (subTotal + discount - tax).toFixed(2);
  // },
  //   align: "right",
  //       },
  // {
  //   title: "Discount",
  //   dataIndex: "total_discount",
  //   render: money,
  //   align: "right",
  //   width: 100,
  // },
  // { title: taxLabel, dataIndex: "total_tax", render: money, align: "right", width: 90, },
  // showGstColumns && {
  //   title: "CGST",
  //   dataIndex: "cgst_amount",
  //   render: numberFormatTax,
  //   align: "right",
  // },
  //   showGstColumns && {
  //     title: "SGST",
  //     dataIndex: "sgst_amount",
  //     render: numberFormatTax,
  //     align: "right",
  //   },
  //   showGstColumns && {
  //     title: "IGST",
  //     dataIndex: "igst_amount",
  //     render: numberFormatTax,
  //     align: "right",
  //   },
  // {
  //   title: "Service",
  //   dataIndex: "other_service_cost",
  //   render: money,
  //   align: "right",
  //   width: 120,
  // },
  // {
  //   title: "Total",
  //   dataIndex: "total_amount",
  //   render: money,
  //   align: "right",
  //   width: 120,
  // },
  // {
  //   title: "Paid",
  //   dataIndex: "paid_total",
  //   render: money,
  //   align: "right",
  //   width: 120,
  // },
  //   { title: "Due", dataIndex: "due_total", render: money, align: "right", width: 120, },
  //   isTatmeenUAE && {
  //     title: "Tatmeen Status",
  //     dataIndex: "tatmeen_sent_status",
  //     width: 140,
  //     align: "center",
  //     render: (value) => {
  //       const colors = {
  //         pending: "#faad14",
  //         sent: "#52c41a",
  //         failed: "#ff4d4f",
  //         cancelled: "#8c8c8c",
  //       };

  //       return (
  //         <span
  //           style={{
  //             color: colors[value] || "#8c8c8c",
  //             fontWeight: 600,
  //             textTransform: "capitalize",
  //           }}
  //         >
  //           {value || "pending"}
  //         </span>
  //       );
  //     },
  //   },
  //   // Add this column after Tatmeen Status column
  //   isTatmeenUAE && {
  //     title: "Unpack Status",
  //     dataIndex: "tatmeen_unpack_status",
  //     width: 140,
  //     align: "center",
  //     render: (value, record) => {
  //       // You may get this from sscc_tracking via the API
  //       const unpackStatus = record.unpack_status || value || "not_started";
  //       const colors = {
  //         not_started: "#8c8c8c",
  //         pending: "#faad14",
  //         sent: "#52c41a",
  //         failed: "#ff4d4f",
  //         unpacked: "#52c41a"
  //       };

  //       const labels = {
  //         not_started: "Not Started",
  //         pending: "Pending",
  //         sent: "Unpacked",
  //         failed: "Failed",
  //         unpacked: "Unpacked"
  //       };

  //       return (
  //         <span
  //           style={{
  //             color: colors[unpackStatus] || "#8c8c8c",
  //             fontWeight: 600,
  //             textTransform: "capitalize"
  //           }}
  //         >
  //           {labels[unpackStatus] || unpackStatus || "Not Started"}
  //         </span>
  //       );
  //     },
  //   },
  //   !printMode && {
  //     title: "Actions",
  //     className: "no-print",
  //     align: "center",
  //     fixed: "right",
  //     width: 110,
  //     // ✅ use PurchaseActions component
  //     render: (_, record) => <PurchaseActions record={record} />,
  //   },
  //     ].filter(Boolean),
  // [printMode, dispatch, filters, navigate, isAdminUser],
  // );

  // ─── Columns ────────────────────────────────────────────────────────────────
  const summaryColumns = useMemo(() => [
    { title: "No.", render: (_, __, i) => i + 1, width: 60, fixed: !isMobile && "left" },
    { title: "Voucher No", dataIndex: "vch_no", fixed: !isMobile && "left" },
    { title: "Date", dataIndex: "date", render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-") },
    { title: "Supplier / Creditor", dataIndex: "supplier" },
    { title: "Invoice No", dataIndex: "pur_invoice_no" },
    { title: "Created By", dataIndex: "createdBy" },
    {
      title: "Sub Total", width: 110, dataIndex: "sub_total",
      render: (_, r) => (Number(r.sub_total || 0) + Number(r.total_discount || 0) - Number(r.total_tax || 0)).toFixed(2),
      align: "right",
    },
    { title: "Discount", dataIndex: "total_discount", render: money, align: "right", width: 100 },
    showTax && { title: taxLabel, dataIndex: "total_tax", render: money, align: "right", width: 90 },
    showGstColumns && { title: "CGST", dataIndex: "cgst_amount", render: numberFormatTax, align: "right" },
    showGstColumns && { title: "SGST", dataIndex: "sgst_amount", render: numberFormatTax, align: "right" },
    showGstColumns && { title: "IGST", dataIndex: "igst_amount", render: numberFormatTax, align: "right" },
    { title: "Service", dataIndex: "other_service_cost", render: money, align: "right", width: 120 },
    { title: "Total", dataIndex: "total_amount", render: money, align: "right", width: 120 },
    { title: "Paid", dataIndex: "paid_total", render: money, align: "right", width: 120 },
    { title: "Due", dataIndex: "due_total", render: money, align: "right", width: 120 },
    isTatmeenUAE && hasTatmeenEntryAccess && {
      title: "Tatmeen Status",
      dataIndex: "tatmeen_sent_status",
      width: 140,
      align: "center",
      render: (value) => {
        const colors = {
          pending: "#faad14",
          sent: "#52c41a",
          failed: "#ff4d4f",
          cancelled: "#8c8c8c",
        };

        return (
          <span
            style={{
              color: colors[value] || "#8c8c8c",
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          >
            {value || "pending"}
          </span>
        );
      },
    },
    // Add this column after Tatmeen Status column
    // isTatmeenUAE && {
    //   title: "Unpack Status",
    //   dataIndex: "tatmeen_unpack_status",
    //   width: 140,
    //   align: "center",
    //   render: (value, record) => {
    //     // You may get this from sscc_tracking via the API
    //     const unpackStatus = record.unpack_status || value || "not_started";
    //     const colors = {
    //       not_started: "#8c8c8c",
    //       pending: "#faad14",
    //       sent: "#52c41a",
    //       failed: "#ff4d4f",
    //       unpacked: "#52c41a"
    //     };

    //     const labels = {
    //       not_started: "Not Started",
    //       pending: "Pending",
    //       sent: "Unpacked",
    //       failed: "Failed",
    //       unpacked: "Unpacked"
    //     };

    //     return (
    //       <span
    //         style={{
    //           color: colors[unpackStatus] || "#8c8c8c",
    //           fontWeight: 600,
    //           textTransform: "capitalize"
    //         }}
    //       >
    //         {labels[unpackStatus] || unpackStatus || "Not Started"}
    //       </span>
    //     );
    //   },
    // },
    !printMode && {
      title: "Actions", className: "no-print", align: "center",
      fixed: "right", width: 150,
      render: (_, record) => <PurchaseActions record={record} />,
    },
  ].filter(Boolean), [printMode, filters, isMobile, isAdminUser]);

  const withItemColumns = useMemo(() => [
    { title: "No.", render: (_, __, i) => i + 1, width: 60 },
    { title: "VCH No", dataIndex: "vch_no" },
    { title: "Date", dataIndex: "date", render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-") },
    { title: "Supplier / Creditor", dataIndex: "supplier" },
    { title: "Invoice No", dataIndex: "pur_invoice_no" },
    { title: "Created By", dataIndex: "createdBy" },
    { title: "Item", dataIndex: "item_name" },
    { title: "Item Code", dataIndex: "display_text" },
    { title: "Rate", dataIndex: "item_rate", render: money, align: "right" },
    { title: "Qty", dataIndex: "item_qty", align: "right" },
    { title: "Discount%", dataIndex: "item_discount_per", render: money, align: "right" },
    showTax && { title: "VAT%", dataIndex: "item_tax_per", render: money, align: "right" },
    { title: "Total", dataIndex: "item_total", render: money, align: "right" },
    !printMode && {
      title: "Actions", align: "center", fixed: "right", width: 120,
      render: (_, record) => <WithItemActions record={record} />,
    },
  ].filter(Boolean), [printMode, filters, isAdminUser, showTax]);

  const byItemColumns = useMemo(() => [
    { title: "No.", render: (_, __, i) => i + 1 },
    { title: "Voucher No", dataIndex: "vch_no" },
    { title: "Date", dataIndex: "date", render: (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-") },
    { title: "Supplier", dataIndex: "supplier" },
    { title: "Item Name", dataIndex: "item_name" },
    { title: "Item Code", dataIndex: "display_text" },
    { title: "Qty", dataIndex: "item_qty", align: "right" },
    { title: "Rate", dataIndex: "item_rate", render: money, align: "right" },
    { title: "Discount%", dataIndex: "item_discount_per", render: money, align: "right" },
    showTax && { title: "VAT%", dataIndex: "item_tax_per", render: money, align: "right" },
    { title: "Total", dataIndex: "item_total", render: money, align: "right" },
  ].filter(Boolean), [showTax]);

  const byTagColumns = useMemo(() => [
    ...byItemColumns,
    { title: "Tag Group", dataIndex: "group_name" },
    { title: "Tag Field", dataIndex: "field_name" },
  ], [byItemColumns]);

  const columns = useMemo(() => {
    switch (filters.filterType) {
      case "By Group":
      case "By Category":
      case "By Item": return byItemColumns;
      case "By Tag": return byTagColumns;
      default:
        return filters.recordType === "with item" ? withItemColumns : summaryColumns;
    }
  }, [filters, summaryColumns, withItemColumns, byItemColumns, byTagColumns]);

  // ─── Totals ─────────────────────────────────────────────────────────────────
  const totals = list.reduce((acc, row) => {
    if (filters.recordType === "with item") {
      acc.masterQty += Number(row.item_qty || 0);
      acc.baseQty += Number(row.retail_qty || 0);
      acc.total += Number(row.item_total || row.total_amount || 0);
    } else if (["By Item", "By Group", "By Category", "By Tag"].includes(filters.filterType)) {
      acc.total += Number(row.item_total || row.total || 0);
    } else {
      acc.subtotal += Number(row.sub_total || 0);
      acc.discount += Number(row.total_discount || 0);
      acc.vat += Number(row.total_tax || 0);
      acc.cgst += Number(row.cgst_amount || 0);
      acc.sgst += Number(row.sgst_amount || 0);
      acc.igst += Number(row.igst_amount || 0);
      acc.service += Number(row.other_service_cost || 0);
      acc.total += Number(row.total_amount || 0);
      acc.paid += Number(row.paid_total || 0);
      acc.due += Number(row.due_total || 0);
    }
    return acc;
  }, {
    masterQty: 0, baseQty: 0, subtotal: 0, discount: 0, vat: 0,
    cgst: 0, sgst: 0, igst: 0, service: 0, total: 0, paid: 0, due: 0
  });

  const expandable = filters.recordType === "with item"
    ? {
      expandedRowRender: (r) => <div style={{ fontSize: 12, color: "#555" }}>{r.unitDetails}</div>,
      rowExpandable: (r) => !!r.unitDetails,
    }
    : undefined;

  if (printMode) {
    const isWithItem = filters.recordType === "with item";
    const isByDetail = ["By Item", "By Group", "By Category", "By Tag"].includes(
      filters.filterType
    );
    const printColumns = columns.filter(
      (col) => !["Created By", "CGST", "SGST", "IGST", "Invoice No"].includes(col.title)
    );
    const identityColCount = printColumns.filter((col) =>
      ["No.", "Voucher No", "VCH No", "Date", "Supplier / Creditor", "Supplier"].includes(col.title)
    ).length;
    const colWidths = getPrintColWidths(printColumns);

    return (
      <table className="ledger-print-table" style={{ tableLayout: "auto" }}
      >
        <colgroup>
          {colWidths.map((w, idx) => (
            <col key={idx} style={{ width: `${w}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr className="ledger-print-heading">
            {printColumns.map((col, idx) => (
              <td key={idx} style={col.align === "right" ? { textAlign: "right" } : undefined}>
                {col.title}
              </td>
            ))}
          </tr>
        </thead>

        <tbody>
          {list.map((row, rowIndex) => (
            <tr key={row.id || rowIndex}>
              {printColumns.map((col, colIndex) => {
                const val = row[col.dataIndex];
                return (
                  <td
                    key={colIndex}
                    style={{
                      ...(col.align === "right" ? { textAlign: "right" } : {}),
                      ...(col.title === "Voucher No" || col.title === "VCH No"
                        ? {
                          whiteSpace: "nowrap",
                          fontSize: "11px",
                        }
                        : {}),
                    }}
                  >
                    {col.render ? col.render(val, row, rowIndex) : val}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>

        {list.length > 0 && (
          <tfoot>
            {isWithItem ? (
              <tr className="ledger-total-row">
                <td colSpan={printColumns.length - 1} style={{ textAlign: "right" }}>
                  Grand Total : Master U. Qty = {totals.masterQty.toFixed(2).replace(/\.00$/, "")},
                  {" "}Base U. Qty = {totals.baseQty.toFixed(2).replace(/\.00$/, "")}
                </td>
                <td style={{ textAlign: "right" }}>{totals.total.toFixed(2)}</td>
              </tr>
            ) : isByDetail ? (
              <tr className="ledger-total-row">
                <td colSpan={printColumns.length - 1} style={{ textAlign: "right" }}>
                  Grand Total
                </td>
                <td style={{ textAlign: "right" }}>{totals.total.toFixed(2)}</td>
              </tr>
            ) : (
              <tr className="ledger-total-row">
                <td colSpan={identityColCount} style={{ textAlign: "right" }}>
                  Grand Total
                </td>
                <td style={{ textAlign: "right" }}>{totals.subtotal.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>{totals.discount.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>{totals.vat.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>{totals.service.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>{totals.total.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>{totals.paid.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>{totals.due.toFixed(2)}</td>
              </tr>
            )}
          </tfoot>
        )}
      </table>
    );
  }
  return (
    <>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        pagination={{ ...pagination, showSizeChanger: true, pageSizeOptions: ["10", "20", "50"] }}
        onChange={handleTableChange}
        bordered
        expandable={expandable}
        scroll={printMode ? undefined : { x: columns.reduce((s, c) => s + (c.width || 150), 0), y: "65vh" }}
        tableLayout="fixed"
        size={printMode ? "small" : "middle"}
        data-testid="purchase-record-table"
        summary={() => {
          if (!list.length) return null;

          if (filters.recordType === "with item") {
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={13}>
                  <strong>Grand Total : Master U. Qty = {totals.masterQty.toFixed(2).replace(/\.00$/, "")}, Base U. Qty = {totals.baseQty.toFixed(2).replace(/\.00$/, "")}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10} align="right"><strong>{totals.total.toFixed(2)}</strong></Table.Summary.Cell>
                {!printMode && <Table.Summary.Cell index={11} />}
              </Table.Summary.Row>
            );
          }

          if (["By Item", "By Group", "By Category", "By Tag"].includes(filters.filterType)) {
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}><strong>Grand Total</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={5} />
                <Table.Summary.Cell index={6} align="right"><strong>{totals.total.toFixed(2)}</strong></Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }

          const taxColOffset = (showTax ? 1 : 0) + (showGstColumns ? 3 : 0);
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}><strong>Grand Total</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={5} />
              <Table.Summary.Cell index={6} align="right"><strong>{totals.subtotal.toFixed(2)}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right"><strong>{totals.discount.toFixed(2)}</strong></Table.Summary.Cell>
              {showTax && <Table.Summary.Cell index={8} align="right"><strong>{totals.vat.toFixed(2)}</strong></Table.Summary.Cell>}
              {showGstColumns && <Table.Summary.Cell index={9} align="right"><strong>{numberFormatTax(totals.cgst)}</strong></Table.Summary.Cell>}
              {showGstColumns && <Table.Summary.Cell index={10} align="right"><strong>{numberFormatTax(totals.sgst)}</strong></Table.Summary.Cell>}
              {showGstColumns && <Table.Summary.Cell index={11} align="right"><strong>{numberFormatTax(totals.igst)}</strong></Table.Summary.Cell>}
              <Table.Summary.Cell index={8 + taxColOffset} align="right"><strong>{totals.service.toFixed(2)}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={9 + taxColOffset} align="right"><strong>{totals.total.toFixed(2)}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={10 + taxColOffset} align="right"><strong>{totals.paid.toFixed(2)}</strong></Table.Summary.Cell>
              <Table.Summary.Cell index={11 + taxColOffset} align="right"><strong>{totals.due.toFixed(2)}</strong></Table.Summary.Cell>
              {!printMode && <Table.Summary.Cell index={12 + taxColOffset} />}
            </Table.Summary.Row>
          );
        }}
      />

      <Modal
        open={tatmeenModal.open}
        width={700}
        footer={
          tatmeenModal.record?.tatmeen_sent_status === "sent"
            ? [
              // <Button
              //   key="unpack"
              //   type="primary"
              //   loading={retryLoading}
              //   icon={<InboxOutlined />}
              //   onClick={() => handleTatmeenUnpack(tatmeenModal.record)}
              // >
              //   Unpack
              // </Button>
            ]
            : (
              tatmeenModal.record?.tatmeen_sent_status === "failed" ||
              tatmeenModal.record?.tatmeen_sent_status === "not_started"
            )
              ? [
                <Button
                  key="retry"
                  type="primary"
                  loading={retryLoading}
                  icon={<RedoOutlined />}
                  onClick={() => handleTatmeenRetry(tatmeenModal.record)}
                >
                  Mark Recieve Entry
                </Button>
              ]
              : null
        }
        onCancel={() =>
          setTatmeenModal({
            open: false,
            record: null,
          })
        }
      >
        {tatmeenModal.record && (
          <>
            {/* Existing Tatmeen Details */}
            <Descriptions bordered column={1} title="Tatmeen Details">
              <Descriptions.Item label="SSCC Code">
                {tatmeenModal.record.sscc_code || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Tatmeen Status">
                {tatmeenModal.record.tatmeen_sent_status || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="SSCC Status">
                {tatmeenModal.record.tatmeen_sscc_status || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Message ID">
                {tatmeenModal.record.tatmeen_message_id || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Retry Count">
                {tatmeenModal.record.tatmeen_retry_count || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Failure Reason">
                {tatmeenModal.record?.tatmeen_failure_reason ? (
                  <div
                    style={{
                      maxHeight: 200,
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "monospace",
                      fontSize: 11,
                      background: "#fafafa",
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    {tatmeenModal.record.tatmeen_failure_reason}
                  </div>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Manual Action Required">
                {tatmeenModal.record.tatmeen_manual_action_required || "-"}
              </Descriptions.Item>
              {/* <Descriptions.Item label="Last Retry At">
                {tatmeenModal.record.tatmeen_last_retry_at || "-"}
              </Descriptions.Item> */}
            </Descriptions>

            {/* NEW - Unpack Details Section */}
            {/* {(tatmeenModal.record.unpack_status ||
              tatmeenModal.record.unpacked_at ||
              tatmeenModal.record.unpack_message_id) && (
                <Descriptions
                  bordered
                  column={1}
                  title="Unpack Details"
                  style={{ marginTop: 16 }}
                >
                  <Descriptions.Item label="Unpack Status">
                    <span
                      style={{
                        color:
                          tatmeenModal.record.unpack_status === "sent" ? "#52c41a" :
                            tatmeenModal.record.unpack_status === "failed" ? "#ff4d4f" :
                              tatmeenModal.record.unpack_status === "pending" ? "#faad14" : "#8c8c8c",
                        fontWeight: 600,
                        textTransform: "capitalize"
                      }}
                    >
                      {tatmeenModal.record.unpack_status || "not_started"}
                    </span>
                  </Descriptions.Item>

                  {tatmeenModal.record.unpacked_at && (
                    <Descriptions.Item label="Unpacked At">
                      {new Date(tatmeenModal.record.unpacked_at).toLocaleString()}
                    </Descriptions.Item>
                  )}

                  {tatmeenModal.record.unpack_message_id && (
                    <Descriptions.Item label="Unpack Message ID">
                      {tatmeenModal.record.unpack_message_id}
                    </Descriptions.Item>
                  )}

                  {tatmeenModal.record.unpack_failure_reason && (
                    <Descriptions.Item label="Unpack Failure Reason">
                      <span style={{ color: "#ff4d4f" }}>
                        {tatmeenModal.record.unpack_failure_reason}
                      </span>
                    </Descriptions.Item>
                  )}

                  {tatmeenModal.record.unpack_retry_count > 0 && (
                    <Descriptions.Item label="Unpack Retry Count">
                      {tatmeenModal.record.unpack_retry_count}
                    </Descriptions.Item>
                  )}

                  {tatmeenModal.record.unpack_last_retry_at && (
                    <Descriptions.Item label="Last Unpack Retry">
                      {new Date(tatmeenModal.record.unpack_last_retry_at).toLocaleString()}
                    </Descriptions.Item>
                  )}

                  {tatmeenModal.record.unpack_manual_action_required === "yes" && (
                    <Descriptions.Item label="Manual Action Required">
                      <span style={{ color: "#ff4d4f", fontWeight: 600 }}>Yes</span>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              )} */}
          </>
        )}
      </Modal>
    </>
  );
};

export default PurchaseRecordTable;